/**
 * ASIN -> product metadata lookup for the enhanced affiliate card block.
 * Merges entries from the two static picks files into an ASIN-keyed map.
 * Returns null silently when an ASIN isn't in either file — callers are
 * expected to degrade gracefully (e.g. render the card without image/price).
 */

import { isValidAsin } from '@/lib/affiliate'
import { amazonImageUrl } from '@/lib/amazon-image'
import { ARTICLE_PRODUCT_PICKS } from '@/data/article-product-picks'
import { getAllGuideProducts } from '@/data/guide-product-recommendations'
import { AMAZON_PRODUCT_ENRICHMENT } from '@/data/amazon-product-enrichment'

export interface LookupResult {
  name: string
  price: string
  imageUrl: string | null
  amazonTitle?: string
  listPrice?: string
}

const ASIN_REGEX = /\/dp\/([A-Z0-9]{10})(?=[/?#]|$)/i

export function extractAsin(affiliateUrl: string): string | null {
  const m = affiliateUrl.match(ASIN_REGEX)
  if (!m) return null
  const asin = m[1].toUpperCase()
  return isValidAsin(asin) ? asin : null
}

export type AffiliateSource = 'article-picks' | 'guide-catalog' | 'enrichment'

export interface AffiliateSourceRecord extends LookupResult {
  asin: string
  source: AffiliateSource
}

/**
 * Every product record from the merged static sources, in merge-precedence
 * order (article picks, then guide catalog, then enrichment). This is the
 * production input to the first-write-wins merge below; the ASIN-consistency
 * test imports it so CI exercises the exact records production serves.
 */
export function listAffiliateSourceRecords(): AffiliateSourceRecord[] {
  const records: AffiliateSourceRecord[] = []

  const add = (
    source: AffiliateSource,
    affiliateUrl: string,
    name: string,
    price: string,
    imageUrl?: string
  ) => {
    const asin = extractAsin(affiliateUrl)
    if (!asin) return
    records.push({ asin, source, name, price, imageUrl: imageUrl ?? null })
  }

  for (const picks of Object.values(ARTICLE_PRODUCT_PICKS)) {
    for (const p of picks) add('article-picks', p.affiliateUrl, p.name, p.price, p.imageUrl)
  }

  for (const rec of getAllGuideProducts()) {
    add('guide-catalog', rec.affiliateUrl, rec.name, rec.price, rec.imageUrl)
  }

  for (const [asin, entry] of Object.entries(AMAZON_PRODUCT_ENRICHMENT)) {
    const imageUrl = entry.imageUrl
      ?? (entry.imageId ? amazonImageUrl(entry.imageId) : null)
    records.push({
      asin,
      source: 'enrichment',
      name: entry.name,
      price: entry.price,
      imageUrl,
      amazonTitle: entry.amazonTitle,
      listPrice: entry.listPrice,
    })
  }

  return records
}

function buildLookupMap(): Map<string, LookupResult> {
  const map = new Map<string, LookupResult>()
  for (const { asin, source: _source, ...rest } of listAffiliateSourceRecords()) {
    if (map.has(asin)) continue
    map.set(asin, rest)
  }
  return map
}

let cachedMap: Map<string, LookupResult> | null = null
function getMap(): Map<string, LookupResult> {
  if (!cachedMap) cachedMap = buildLookupMap()
  return cachedMap
}

export function getProductByAsin(asin: string | null | undefined): LookupResult | null {
  if (!asin) return null
  return getMap().get(asin) ?? null
}

/**
 * Case-insensitive + alphanumeric-only prefix check: returns true when the Amazon
 * listing title diverges meaningfully from the editorial product name. Used to
 * flag anchor-text / actual-product drift (e.g. "Southern Triple Action" anchor
 * pointing to a non-Southern SKU). Neither string being a prefix of the other
 * counts as drift.
 */
export function titleIsMeaningfullyDifferent(editorial: string, amazon: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const e = norm(editorial)
  const a = norm(amazon)
  if (!e || !a) return false
  return !a.startsWith(e) && !e.startsWith(a)
}

/**
 * Count how many products in a parsed block have a resolvable imageUrl.
 * imageUrl is the coverage bar — price is tolerated as temporarily missing.
 */
export function countCoveredProducts(
  products: Array<{ asin: string | null }>
): number {
  let n = 0
  for (const p of products) {
    const lookup = getProductByAsin(p.asin)
    if (lookup?.imageUrl) n++
  }
  return n
}
